import {
  joinSessionFunction,
  leaveSessionFunction,
  getSessionStatsFunction,
  createSessionFunction,
  updateSessionFunction,
  deleteSessionFunction,
  logSessionFunction,
  type JoinSessionData,
  type LeaveSessionData,
  type GetSessionStatsData,
  type CreateSessionData,
  type UpdateSessionData,
  type DeleteSessionData,
  type LogSessionData,
  type JoinSessionResponse,
  type LeaveSessionResponse,
  type SessionStatsResponse,
  type CreateSessionResponse,
  type UpdateSessionResponse,
  type DeleteSessionResponse,
  type LogSessionResponse,
  dmAddPlayerToSessionFunction,
  claimDMTokenFunction,
  getDMTokenClaimsFunction,
} from "../firebase/functions";
import {httpsCallable} from "firebase/functions";
import {functions} from "../firebase/config";

export class SessionService {
  /**
   * Join a session using cloud function
   */
  static async joinSession(data: JoinSessionData): Promise<void> {
    try {
      const result = await joinSessionFunction(data);
      const response = result.data as JoinSessionResponse;

      if (!response.success) {
        throw new Error(response.message || "Failed to join session");
      }
    } catch (error: any) {
      throw new Error(error.message || "Failed to join session");
    }
  }

  /**
   * DM adds a player (and character) to a session
   */
  static async dmAddPlayerToSession(data: {
    sessionId: string;
    playerId: string;
    characterId: string;
  }): Promise<{success: boolean; message: string}> {
    try {
      const result = await dmAddPlayerToSessionFunction(data);
      const response = result.data as {success: boolean; message: string};
      if (!response.success) {
        throw new Error(response.message || "Failed to add player to session");
      }
      return response;
    } catch (error: any) {
      throw new Error(error.message || "Failed to add player to session");
    }
  }

  /**
   * Leave a session using cloud function
   */
  static async leaveSession(data: LeaveSessionData): Promise<void> {
    try {
      const result = await leaveSessionFunction(data);
      const response = result.data as LeaveSessionResponse;

      if (!response.success) {
        throw new Error(response.message || "Failed to leave session");
      }
    } catch (error: any) {
      throw new Error(error.message || "Failed to leave session");
    }
  }

  /**
   * Get session statistics using cloud function
   */
  static async getSessionStats(
    data: GetSessionStatsData
  ): Promise<SessionStatsResponse["stats"]> {
    try {
      const result = await getSessionStatsFunction(data);
      const response = result.data as SessionStatsResponse;

      return response.stats;
    } catch (error: any) {
      throw new Error(error.message || "Failed to get session statistics");
    }
  }

  /**
   * Create a session using cloud function
   */
  static async createSession(data: CreateSessionData): Promise<string> {
    try {
      const result = await createSessionFunction(data);
      const response = result.data as CreateSessionResponse;

      if (!response.success) {
        throw new Error(response.message || "Failed to create session");
      }

      return response.sessionId || "";
    } catch (error: any) {
      throw new Error(error.message || "Failed to create session");
    }
  }

  /**
   * Update a session using cloud function
   */
  static async updateSession(data: UpdateSessionData): Promise<void> {
    try {
      const result = await updateSessionFunction(data);
      const response = result.data as UpdateSessionResponse;

      if (!response.success) {
        throw new Error(response.message || "Failed to update session");
      }
    } catch (error: any) {
      throw new Error(error.message || "Failed to update session");
    }
  }

  /**
   * Delete a session using cloud function
   */
  static async deleteSession(data: DeleteSessionData): Promise<void> {
    try {
      const result = await deleteSessionFunction(data);
      const response = result.data as DeleteSessionResponse;

      if (!response.success) {
        throw new Error(response.message || "Failed to delete session");
      }
    } catch (error: any) {
      throw new Error(error.message || "Failed to delete session");
    }
  }

  /**
   * Log session participation using cloud function
   */
  static async logSession(data: LogSessionData): Promise<LogSessionResponse> {
    try {
      const result = await logSessionFunction(data);
      const response = result.data as LogSessionResponse;

      if (!response.success) {
        throw new Error(response.message || "Failed to log session");
      }

      return response;
    } catch (error: any) {
      throw new Error(error.message || "Failed to log session");
    }
  }

  /**
   * Check if a session has been logged for a specific character
   */
  static async checkSessionLogStatus(data: {
    sessionId: string;
    playerId: string;
    characterId: string;
  }): Promise<{success: boolean; hasLogged: boolean}> {
    try {
      const checkSessionLogStatusFunction = httpsCallable(
        functions,
        "checkSessionLogStatus"
      );
      const result = await checkSessionLogStatusFunction(data);
      const response = result.data as {success: boolean; hasLogged: boolean};

      if (!response.success) {
        throw new Error("Failed to check session log status");
      }

      return response;
    } catch (error: any) {
      throw new Error(error.message || "Failed to check session log status");
    }
  }

  /**
   * Get existing session log for editing
   */
  static async getSessionLog(data: {
    sessionId: string;
    playerId: string;
    characterId: string;
  }): Promise<{
    success: boolean;
    logData: {
      events: string;
      currency: {
        platinum: number;
        gold: number;
        electrum: number;
        silver: number;
        copper: number;
      };
      items: Array<{
        name: string;
        quantity: number;
        description: string;
        attunement: boolean;
      }>;
    };
  }> {
    try {
      const getSessionLogFunction = httpsCallable(functions, "getSessionLog");
      const result = await getSessionLogFunction(data);
      const response = result.data as {
        success: boolean;
        logData: {
          events: string;
          currency: {
            platinum: number;
            gold: number;
            electrum: number;
            silver: number;
            copper: number;
          };
          items: Array<{
            name: string;
            quantity: number;
            description: string;
            attunement: boolean;
          }>;
        };
      };

      if (!response.success) {
        throw new Error("Failed to get session log");
      }

      return response;
    } catch (error: any) {
      throw new Error(error.message || "Failed to get session log");
    }
  }

  /**
   * Update existing session log
   */
  static async updateSessionLog(
    data: LogSessionData
  ): Promise<{success: boolean; message: string}> {
    try {
      const updateSessionLogFunction = httpsCallable(
        functions,
        "updateSessionLog"
      );
      const result = await updateSessionLogFunction(data);
      const response = result.data as {success: boolean; message: string};

      if (!response.success) {
        throw new Error(response.message || "Failed to update session log");
      }

      return response;
    } catch (error: any) {
      throw new Error(error.message || "Failed to update session log");
    }
  }
  /**
   * Get DM token claims for a DM
   */
  static async getDMTokenClaims(dmId?: string): Promise<{
    success: boolean;
    claims: Array<{
      id: string;
      sessionId: string;
      sessionName: string;
      claimedAt: any;
      tokensAwarded: number;
    }>;
    message: string;
  }> {
    try {
      const result = await getDMTokenClaimsFunction({dmId});
      return result.data as any;
    } catch (error: any) {
      throw new Error(error.message || "Failed to get DM token claims");
    }
  }

  /**
   * Claim DM token for a completed session
   */
  static async claimDMToken(sessionId: string): Promise<{
    success: boolean;
    message: string;
    tokensAwarded: number;
    totalTokens: number;
  }> {
    try {
      const result = await claimDMTokenFunction({sessionId});
      return result.data as any;
    } catch (error: any) {
      throw new Error(error.message || "Failed to claim DM token");
    }
  }
}
